import { Field } from 'formik';
import { COOKIE_AUTH_DETAILS, pageUrls } from 'globalconfig/constants/keys';
import { getCookie } from 'globalservices/cookie';
import { getFilterLocaleValues, hasEnabledFeature, objectArrayFromSameField } from 'globalutils/fmUtils';
import React, { lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;
const CheckBoxToggle = lazy(() => import(`globalcomponents/UI/Checkbox/${layout}`));
const Link = lazy(() => import(`globalcomponents/UI/Link`));
const RadioButton = lazy(() => import(`globalcomponents/UI/Radio/${layout}`));

export default function LeftForm({
  Styled,
  values,
  handleEdit,
  showError,
  errors,
  isNotValid,
  hasSaveApiStatus,
  dotUpdation,
  type,
}) {
  const SERVICE_PROVIDER = window.$environment.SERVICE_PROVIDER;
  const i18n = useTranslation();
  const maintPeriod = objectArrayFromSameField([1, 2, 3, 4, 5, 6, 7]);
  const token = getCookie(COOKIE_AUTH_DETAILS);

  return (
    <Styled.leftWrapper>
      <div className="companyForm firstRow">
        <FormStyle.FieldsWrapper>
          <Label htmlFor="company" variant="body1">
            {i18n.t('companies.companyName')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="company"
              handleOnChange={(event) => handleEdit(event, { field: 'company' })}
              as={Input}
              placeholder={i18n.t('companies.companyName')}
              value={values['company']}
              type="text"
              className="borderStyle"
              id="company"
              onLoadFocus
            />
            {showError('company', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="dotNumber" variant="body1">
            {i18n.t('common.quoteId')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="dotNumber"
              id="dotNumber"
              handleOnChange={(event) => handleEdit(event, { field: 'dotNumber' })}
              as={Input}
              placeholder={i18n.t('common.quoteId')}
              value={values['dotNumber']}
              type="text"
              updateValue={(e) => dotUpdation(e)}
            />
            {showError('dotNumber', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        {layout !== 'Clutch' && (
          <>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="maintenancePeriod" variant="body1">
                {i18n.t('companyData.maintainancePeriodMonth')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="maintenancePeriod"
                  id="maintenancePeriod"
                  onChange={(event) => handleEdit(event, { field: 'maintenancePeriod' })}
                  as={Select}
                  placeholderText={i18n.t('companyData.maintainancePeriod')}
                  value={getFilterLocaleValues([values['maintenancePeriod']], i18n)}
                  suggestions={getFilterLocaleValues(maintPeriod, i18n)}
                />
                {showError('maintenancePeriod', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="maintenanceMileage" variant="body1">
                {i18n.t('companyData.maintainanceMileage')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="maintenanceMileage"
                  id="maintenanceMileage"
                  handleOnChange={(event) => handleEdit(event, { field: 'maintenanceMileage' })}
                  as={Input}
                  placeholder={i18n.t('companyData.maintainanceMileage')}
                  value={values['maintenanceMileage']}
                  type="text"
                  className="borderStyle"
                />
                {showError('maintenanceMileage', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <FormStyle.TextWrapper className="subscriptionCompany">
                <Link id="subscriptionTier" newPage href={pageUrls.subscriptionLink}>
                  {i18n.t('registration.subscriptionTier')}
                </Link>
              </FormStyle.TextWrapper>
              <FormStyle.CheckBoxWrapper direction="row">
                <RadioButton
                  aria-labelledby="subscriptionTier"
                  value={values.subscriptionTier}
                  options={checkServiceProvider('leftform_subscriptionTier_options', SERVICE_PROVIDER, false)}
                  onClick={(event) => {
                    handleEdit(event, {
                      field: 'subscriptionTier',
                      type: 'radio',
                    });
                  }}
                />
              </FormStyle.CheckBoxWrapper>
            </FormStyle.FieldsWrapper>
          </>
        )}
      </div>
      <div className="companyForm">
        {checkServiceProvider('leftform_fields', SERVICE_PROVIDER, false) && (
          <>
            <FormStyle.FieldsWrapper className="valueWrapper">
              <Label variant="body1">{i18n.t('companyData.contractExpirationDate')}</Label>
              <FormStyle.TextWrapper className="values">{values.contractExpirationDate}</FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>

            <FormStyle.FieldsWrapper className="valueWrapper">
              <Label variant="body1">{i18n.t('companyData.currentSubscriptionCost')}</Label>
              <FormStyle.TextWrapper className="values">{values.currentSubscriptionCost}</FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
          </>
        )}
        <FormStyle.FieldsWrapper className="valueWrapper">
          <Label variant="body1">{i18n.t('companyData.noOfActiveTrucks')}</Label>
          <FormStyle.TextWrapper className="values">{values.activeTrucks}</FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        {type === 'edit' && checkServiceProvider('leftform_fields_rolebased', SERVICE_PROVIDER, false) && (
          <FormStyle.FieldsWrapper className="valueWrapper">
            <Label variant="body1">{i18n.t('companyData.noOfCameras')}</Label>
            <FormStyle.TextWrapper className="values">{values.initialTrucks}</FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
        )}
        <FormStyle.FieldsWrapper className="valueWrapper">
          <Label variant="body1">{i18n.t('companyData.homeTimeZone')}</Label>
          <FormStyle.TextWrapper className="values">{values.timeZone}</FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <div className="checkboxesCompany oldviewAlign">
          {token.roles?.includes('FLMANAGER') && (
            <FormStyle.FieldsWrapper htmlFor="skipVehicleFilter" className="agreeTerms">
              <FormStyle.TextWrapper>
                <CheckBoxToggle
                  aria-labelledby="agree"
                  type="NormalGrey"
                  checked={values.skipVehicleFilter}
                  label={i18n.t('companyData.skipcompanyCheckbox')}
                  onClick={(event) =>
                    handleEdit(event, {
                      field: 'skipVehicleFilter',
                    })
                  }
                  name="skipVehicleFilter"
                />
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
          )}
          {token?.roles?.includes('FLMANAGER') && hasEnabledFeature('editOilFieldWaitingEnabled') && (
            <FormStyle.FieldsWrapper htmlFor="showOw" className="agreeTerms">
              <FormStyle.TextWrapper>
                <CheckBoxToggle
                  aria-labelledby="agree"
                  type="NormalGrey"
                  checked={values.showOw}
                  label={i18n.t('companyData.enablecompanyCheckbox')}
                  onClick={(event) =>
                    handleEdit(event, {
                      field: 'showOw',
                    })
                  }
                  name="showOw"
                />
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
          )}
        </div>
      </div>
    </Styled.leftWrapper>
  );
}
